import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Leaf } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/contexts/AuthContext'
import { isApiError } from '@/lib/api'

const schema = z.object({
  orgName: z.string().trim().min(1, "Nom d'entreprise requis"),
  email: z.string().email('Email invalide'),
  password: z.string().min(8, 'Minimum 8 caractères'),
})

type FormValues = z.infer<typeof schema>

export type RegisterProps = {
  onSetupNeeded: () => void
  onLoginRedirect: () => void
}

// Note: `Input` shadcn de ce projet n'est pas un `forwardRef`. RHF.register()
// repose sur le ref callback pour enregistrer le champ — sans forward, le
// champ reste vide et `min(1)` échoue. On utilise donc `Controller` qui pilote
// l'input en mode contrôlé, indépendant du ref.
export function Register({ onSetupNeeded, onLoginRedirect }: RegisterProps) {
  const { register: registerAccount } = useAuth()
  const [submitting, setSubmitting] = useState(false)
  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: { orgName: '', email: '', password: '' },
  })

  const passwordLength = (watch('password') ?? '').length

  const onSubmit = async (data: FormValues) => {
    setSubmitting(true)
    try {
      await registerAccount(data.orgName.trim(), data.email.trim(), data.password)
      onSetupNeeded()
    } catch (err: unknown) {
      if (isApiError(err)) {
        if (err.status === 409) {
          if (err.message === 'Un compte avec cet email existe déjà') {
            setError('email', { message: err.message })
          } else {
            setError('orgName', { message: err.message })
          }
        } else if (err.status === 422) {
          toast.error(err.message || 'Données invalides')
        } else {
          toast.error(err.message || "Erreur lors de l'inscription")
        }
      } else {
        toast.error('Erreur de connexion, veuillez réessayer')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="max-w-md mx-auto w-full">
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center space-x-2">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-500 shadow-sm">
            <Leaf className="h-5 w-5 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-green-700 text-lg leading-tight">SustainTrack</span>
            <span className="text-xs text-green-600 leading-tight">Créer un compte</span>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 bg-white/70 backdrop-blur-sm p-6 rounded-lg border shadow"
      >
        <div>
          <Label htmlFor="orgName">Nom de l'entreprise</Label>
          <Controller
            name="orgName"
            control={control}
            render={({ field }) => (
              <Input
                id="orgName"
                placeholder="Ma société"
                autoFocus
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
          {errors.orgName && (
            <p className="text-sm text-red-600 mt-1">{errors.orgName.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="email">Email</Label>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                id="email"
                type="email"
                placeholder="vous@entreprise.com"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
          {errors.email && (
            <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="password">Mot de passe</Label>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <Input
                id="password"
                type="password"
                placeholder="Minimum 8 caractères"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {passwordLength === 0
              ? 'Minimum 8 caractères'
              : passwordLength < 8
                ? `${passwordLength}/8 caractères`
                : '✓ Longueur suffisante'}
          </p>
          {errors.password && (
            <p className="text-sm text-red-600 mt-1">{errors.password.message}</p>
          )}
        </div>

        <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={submitting}>
          {submitting ? 'Création en cours…' : 'Créer mon compte'}
        </Button>

        <p className="text-sm text-center text-muted-foreground">
          Déjà un compte ?{' '}
          <button
            type="button"
            onClick={onLoginRedirect}
            className="text-green-700 underline hover:text-green-800"
          >
            Se connecter
          </button>
        </p>
      </form>
    </div>
  )
}
